import {
  ID,
  ListApiResponse,
  SingleApiResponse,
  unwrapApiErrorDetails
} from '@ianesthesie-kit/services';
import { valueOfEnum } from '@ianesthesie-kit/core';
import {
  Medication,
  MedicationAtc,
  MedicationInterruptionAutomationProperty,
  MedicationInterruptionStatus
} from './meds.model';
import { ListMedicalService, MedicalResponse } from '../baseMedical.service';
import axios, { AxiosResponse } from 'axios';
import { isNotEmpty } from '@/util';

export class MedsService extends ListMedicalService<Medication> {
  private static INSTANCE_BY_ID: MedsService;

  private constructor(patientId: string) {
    super('/meds', patientId);
  }

  public static getInstance(patientId: ID): MedsService {
    if (this.shouldCreateNewInstance(this.INSTANCE_BY_ID, patientId)) {
      this.INSTANCE_BY_ID = new MedsService(patientId);
    }
    return this.INSTANCE_BY_ID;
  }

  public static getGlp1PeriodicityTranslation(
    v: Partial<Medication> | Medication['glp1Periodicity']
  ) {
    const val = typeof v === 'string' ? v : v?.glp1Periodicity;
    if (!val) {
      return undefined;
    }

    return `medical_synthesis.medication.glp1Periodicity.${val}`;
  }

  public static getGlp1PeriodicityColor(
    v: Partial<Medication> | Medication['glp1Periodicity']
  ) {
    const val = typeof v === 'string' ? v : v?.glp1Periodicity;
    if (!val) {
      return undefined;
    }

    switch (val) {
      case 'GLP1_UNCERTAIN':
        return 'warning' as const;
      case 'GLP1_EITHER':
        return 'riskMed' as const;
      default:
        return 'info' as const;
    }
  }

  public static fetchMedicationAtcList(
    nameStartingWith = ''
  ): Promise<MedicalResponse<MedicationAtc, true>> {
    return axios
      .get<ListApiResponse<MedicationAtc>>(`/bff/doctor/meds`, {
        params: nameStartingWith
          ? { name: encodeURIComponent(nameStartingWith) }
          : undefined
      })
      .then((response) => ({
        data: response.data.data
      }))
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public static fetchInterruptionAutomationProperties(
    medOrAtc: MedicationAtc | MedicationAtc['atc']
  ): Promise<MedicalResponse<MedicationInterruptionAutomationProperty, true>> {
    const atc = typeof medOrAtc === 'string' ? medOrAtc : medOrAtc?.atc;
    if (!atc) {
      return Promise.resolve({ data: [] });
    }

    return axios
      .get<ListApiResponse<MedicationInterruptionAutomationProperty>>(
        `/bff/doctor/meds-automation-interruption-properties?atc=${atc}`
      )
      .then((response) => ({
        data: response.data.data
      }))
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public dryRunAutomation(
    med: Partial<Medication>
  ): Promise<MedicalResponse<Medication, false>> {
    return axios
      .post(this.getUrl('automation-dry-run'), this.transformToRequestBody(med))
      .then((response: AxiosResponse<SingleApiResponse<Medication>>) => ({
        data: this.transformResponseEntity(response.data.data)
      }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  public upsert(
    item: Partial<Medication>,
    params?: Record<string, any>
  ): Promise<MedicalResponse<Medication, true>> {
    const payload = params?.automation
      ? this.stripAutomationOutputs(item)
      : item;
    return super.upsert(payload, params);
  }

  public setNoKnownMedications(value: boolean): Promise<void> {
    return axios
      .put(`${this.url}/no-known-medications`, null, { params: { value } })
      .then(() => undefined)
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public deleteMany(ids: ID[]): Promise<MedicalResponse<Medication, true>> {
    return this.deleteAt('', { data: { ids } });
  }

  public resetAll(): Promise<MedicalResponse<Medication, true>> {
    return this.deleteAt('all');
  }

  protected transformResponseEntity(data: any): Medication {
    return {
      ...super.transformResponseEntity(data),
      interruptionStatus:
        data.interruptionStatus &&
        valueOfEnum(data.interruptionStatus, MedicationInterruptionStatus, {
          throwIfNotFound: true,
          caseSensitive: false
        })!,
      bridgedBy:
        data.bridgedBy &&
        data.bridgedBy.map((m: Medication) => this.transformResponseEntity(m))
    };
  }

  protected transformToRequestBody(
    data: Partial<Medication>
  ): Record<string, unknown> {
    const sanitized = this.sanitizePayload(data);
    return {
      ...super.transformToRequestBody(sanitized),
      bridgedBy: sanitized?.bridgedBy?.filter(isNotEmpty)
    };
  }

  private stripAutomationOutputs(
    item: Partial<Medication>
  ): Partial<Medication> {
    return {
      ...item,
      interruptionStatus: undefined,
      stopDaysBefore: undefined,
      bridgedBy: undefined,
      otherInterruptionStatusText: undefined,
      valuesAutomated: undefined,
      missingAutomationProperties: undefined
    };
  }

  private sanitizePayload(data: Partial<Medication>) {
    const sanitized = {
      ...data
    };

    if (
      data.interruptionStatus === MedicationInterruptionStatus.STOP_AND_BRIDGE
    ) {
      sanitized.bridgedBy = data?.bridgedBy?.filter(isNotEmpty);
    } else {
      sanitized.bridgedBy = undefined;
    }

    if (
      data.interruptionStatus !==
        MedicationInterruptionStatus.STOP_X_DAYS_BEFORE &&
      data.interruptionStatus !== MedicationInterruptionStatus.STOP_AND_BRIDGE
    ) {
      sanitized.stopDaysBefore = undefined;
    }

    if (
      data.interruptionStatus ===
        MedicationInterruptionStatus.STOP_X_DAYS_BEFORE &&
      data.stopDaysBefore === 0
    ) {
      sanitized.stopDaysBefore = undefined;
      sanitized.interruptionStatus =
        MedicationInterruptionStatus.STOP_ON_THE_DAY_OF_SURGERY;
    }

    if (data.interruptionStatus !== MedicationInterruptionStatus.OTHER) {
      sanitized.otherInterruptionStatusText = undefined;
    }

    return sanitized;
  }
}
